export type ZendeskStatus = "New" | "Open" | "Pending" | "Hold" | "Solved" | "Closed";

export type ZendeskPriority = "Low" | "Normal" | "High" | "Urgent";

export type ZendeskTicketType = "Question" | "Incident" | "Problem" | "Task";

export type ZendeskMode = "Filter" | "View";

/** A Zendesk view the signed-in user can see. */
export interface ZendeskViewReference {
  id: number;
  title: string;
}

/** Mirrors server-side ZendeskConfiguration (camelCase, enums as strings). */
export interface ZendeskConfiguration {
  /** Set by the server at sign-in; read-only in the editor. */
  subdomain?: string | null;
  mode?: ZendeskMode;
  /** Filter mode: statuses to import. */
  statuses?: ZendeskStatus[] | null;
  /** Filter mode: empty or null imports every priority. */
  priorities?: ZendeskPriority[] | null;
  /** Filter mode: empty or null imports every ticket type. */
  ticketTypes?: ZendeskTicketType[] | null;
  /** Filter mode: only tickets assigned to the signed-in user. */
  assignedToMe?: boolean;
  /** Filter mode: space-separated tag list. */
  tags?: string | null;
  /** View mode: the view whose tickets are imported. */
  view?: ZendeskViewReference | null;
  /** Both modes: the status written back when a task is marked done. */
  doneStatus?: ZendeskStatus | null;
}

/** Every Zendesk ticket status, in workflow order. */
export const ZENDESK_STATUSES: readonly ZendeskStatus[] = [
  "New",
  "Open",
  "Pending",
  "Hold",
  "Solved",
  "Closed",
];

/** Every Zendesk ticket priority, lowest first. */
export const ZENDESK_PRIORITIES: readonly ZendeskPriority[] = [
  "Low",
  "Normal",
  "High",
  "Urgent",
];

/** Every Zendesk ticket type. */
export const ZENDESK_TICKET_TYPES: readonly ZendeskTicketType[] = [
  "Question",
  "Incident",
  "Problem",
  "Task",
];

/** The statuses a ticket can be moved to when its task is marked done. */
export const ZENDESK_DONE_STATUSES: readonly ZendeskStatus[] = ["Solved", "Closed"];

/** Statuses imported by default when a connection is first configured. */
export const ZENDESK_DEFAULT_STATUSES: readonly ZendeskStatus[] = ["New", "Open", "Pending"];

/** The done status used by default when a connection is first configured. */
export const ZENDESK_DEFAULT_DONE_STATUS: ZendeskStatus = "Solved";

/** Whether a new connection imports only the tickets assigned to the signed-in agent. */
export const ZENDESK_DEFAULT_ASSIGNED_TO_ME = true;

/** The mode used when none is stored. */
export const ZENDESK_DEFAULT_MODE: ZendeskMode = "Filter";

/** Host suffix appended to the subdomain to show the account the connection signed in to. */
export const ZENDESK_DOMAIN_SUFFIX = ".zendesk.com";
